import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import WalletGearModal, { PageGearButton } from './WalletGearModal';
import HlMarketSearch from './HlMarketSearch';
import HlTradingChart from './HlTradingChart';
import LiveOrderBook from './LiveOrderBook';
import SingleAssetHlPortfolio from './SingleAssetHlPortfolio';
import SingleAssetHlPositionsPanel from './SingleAssetHlPositionsPanel';
import SingleAssetHlOrderTransactions from './SingleAssetHlOrderTransactions';
import HlFundUsdcButton from './HlFundUsdcButton';
import HlFundUsdcModal from './HlFundUsdcModal';
import HlWithdrawModal from './HlWithdrawModal';
import { fmtUsd, fmtDollar } from '../utils/fmtPrice';
import {
  quoteForMarket,
  displayMarketName,
  formatDexLabel,
  hlUsdcBalance,
  hlSpotUsdcBalance,
  hlSpotSolBalance,
  hlUsdhBalance,
  collateralBalanceForMarket,
} from '../utils/hlMarketDisplay';
import { formatHlSlippageLabel } from '../utils/hlSlippage';
import { submitSingleAssetHlTrade } from '../utils/hlSingleAssetSign';
import { moveUsdcSpotToPerpsWithBrowserWallet } from '../utils/hlUsdClassTransfer';
import { resolveStoredHlSlippageBps } from '../utils/hlSlippageStorage';
import { TRADE_ACTION_OPTIONS, sideTabsForAction } from '../utils/tradeActionUi';
import './SingleAssetHLTrading.css';
import './WalletGearModal.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});


function fmtVolume(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return fmtUsd(v);
}

function fmtCommissionLine(quote) {
  if (!quote?.commissionEnabled) return 'None (commission disabled)';
  if (quote.commissionSkipped || !(Number(quote.commissionUsd) > 0)) {
    if (quote.commissionSkipReason === 'zero_bps') return 'None (0 bps configured)';
    if (quote.commissionSkipReason === 'below_minimum') return 'None (below minimum)';
    return 'None';
  }
  const bps = quote.commissionBps != null ? ` · ${quote.commissionBps} bps` : '';
  return `${fmtUsd(quote.commissionUsd)}${bps}`;
}

function marketSelectionKey(m) {
  if (!m?.symbol) return '';
  const sym = m.symbol.toUpperCase();
  const dex = (m.dex || 'xyz').toLowerCase();
  return `${dex}:${sym}`;
}

const ACTION_OPTIONS = TRADE_ACTION_OPTIONS;

export default function SingleAssetHLTrading() {
  const { symbol: routeSymbol } = useParams();
  const [searchParams] = useSearchParams();
  const routeDex = (searchParams.get('dex') || 'xyz').toLowerCase();
  const navigate = useNavigate();
  const location = useLocation();
  const orderTypeRef = useRef('market');
  const loadedRouteRef = useRef('');

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [side, setSide] = useState('long');
  const [action, setAction] = useState('open');
  const [orderType, setOrderType] = useState('market');
  const [sizeUsd, setSizeUsd] = useState('0');
  const [leverage, setLeverage] = useState(1);
  const [limitPrice, setLimitPrice] = useState('');
  const [leverageOptions, setLeverageOptions] = useState([1]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [fundModalOpen, setFundModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [orderbookPollMs, setOrderbookPollMs] = useState(35000);
  const [pendingQuote, setPendingQuote] = useState(null);

  const loadConfig = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/single-asset-hl/config`);
    setConfig(data);
    if (data.orderbookPollMs) setOrderbookPollMs(data.orderbookPollMs);
  }, []);

  const loadStatus = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/single-asset-hl/status`, { headers: authHeaders() });
    setStatus(data);
  }, []);

  const selectMarket = useCallback(
    (m) => {
      if (!m?.symbol) return;
      const dex = m.dex || 'xyz';
      navigate(`/single-asset-hl/${m.symbol}?dex=${encodeURIComponent(dex)}`, { replace: true });
    },
    [navigate]
  );

  orderTypeRef.current = orderType;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  useEffect(() => {
    loadConfig();
    loadStatus();
  }, [loadConfig, loadStatus]);

  // Keep live HL balances fresh while the page is open
  useEffect(() => {
    if (!status?.walletAddress) return undefined;
    const id = setInterval(() => {
      loadStatus().catch(() => {});
    }, 12000);
    return () => clearInterval(id);
  }, [status?.walletAddress, loadStatus]);

  useEffect(() => {
    if (!pendingQuote) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) cancelPendingTrade();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [pendingQuote, busy]);

  useEffect(() => {
    if (!routeSymbol) {
      navigate('/single-asset-hl/BTC?dex=main', { replace: true });
    }
  }, [routeSymbol, navigate]);

  useEffect(() => {
    if (!routeSymbol) {
      loadedRouteRef.current = '';
      setSelected(null);
      setStats(null);
      return undefined;
    }

    const sym = routeSymbol.toUpperCase();
    const dex = routeDex;
    const routeKey = marketSelectionKey({ symbol: sym, dex });
    if (loadedRouteRef.current === routeKey) return undefined;

    setSelected((prev) =>
      prev && marketSelectionKey(prev) === routeKey ? prev : { symbol: sym, dex }
    );
    setStats(null);

    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const { data } = await axios.get(`${API_BASE}/single-asset-hl/market-meta/${sym}`, {
          headers: authHeaders(),
          params: { dex },
        });
        if (cancelled) return;
        loadedRouteRef.current = routeKey;
        setSelected(data);
        setStats(data.stats || null);
        setLeverageOptions(data.leverageOptions || [1]);
        setLeverage((lev) => Math.min(lev, data.maxLeverage || 1));
        if (data.mid != null && orderTypeRef.current === 'limit') {
          setLimitPrice((prev) => prev || String(data.mid));
        }
      } catch (e) {
        if (!cancelled) {
          loadedRouteRef.current = '';
          setStats(null);
          setError(e.response?.data?.error || e.message);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [routeSymbol, routeDex]);

  const notional = (Number(sizeUsd) || 0) * (Number(leverage) || 1);
  const paper = config?.paperMode !== false;
  const marginQuote = quoteForMarket(selected);
  const marketLabel = displayMarketName(selected);
  const selectedDex = formatDexLabel(selected);
  const perpsUsdc = hlUsdcBalance(status);
  const perpsEquity = status?.perpsEquityUsdc;
  const spotUsdc = hlSpotUsdcBalance(status);
  const spotSol = hlSpotSolBalance(status);
  const unifiedAccount = !!status?.unifiedAccount;
  // Allow move whenever spot has USDC on standard (non-unified) accounts.
  const canMoveSpotUsdc = !unifiedAccount && Number(spotUsdc) > 0.01 && status?.walletAddress;
  const readyToTrade = unifiedAccount
    ? Number(perpsUsdc) > 0.01
    : Number(spotUsdc) <= 0.01 && (Number(perpsUsdc) > 0.01 || Number(perpsEquity) > 0.01);

  const signPreparedOrder = async (data) => {
    setMessage('Confirm in your wallet…');
    const submitted = await submitSingleAssetHlTrade({
      walletAddress: data.walletAddress,
      orderId: data.orderId,
      prepared: data,
      apiBase: API_BASE,
      authHeaders,
    });
    setMessage(submitted.message || `Live order submitted for ${marketLabel}.`);
    setRefreshKey((k) => k + 1);
    await loadStatus();
    return submitted;
  };

  const agentApproved = !!status?.agentApproved;
  const liveSignLabel =
    orderType === 'limit'
      ? agentApproved
        ? 'Place limit'
        : 'Sign & place limit'
      : agentApproved
        ? 'Place market'
        : 'Sign & place market';
  const livePrimaryLabel = agentApproved
    ? orderType === 'limit'
      ? 'Place limit'
      : 'Place market'
    : liveSignLabel;

  const submitTrade = async (override = null, { autoSign = false } = {}) => {
    const tradeSymbol = override?.symbol || selected?.symbol;
    const tradeDex = override?.hlDex || selected?.dex;
    const tradeCoin = override?.hlCoin || selected?.coin;
    const tradeSide = override?.side ?? side;
    const tradeAction = override?.action ?? action;
    const tradeSizeUsd = override?.sizeUsd != null ? Number(override.sizeUsd) : Number(sizeUsd);
    const tradeLeverage = override?.leverage != null ? Number(override.leverage) : Number(leverage);
    const tradeOrderType = override?.orderType ?? orderType;
    const tradeLimitPrice = override?.limitPrice != null ? Number(override.limitPrice) : Number(limitPrice);

    if (!tradeSymbol) {
      setError('Select a market first.');
      return;
    }
    if (tradeOrderType === 'limit' && !(tradeLimitPrice > 0)) {
      setError('Enter a valid limit price.');
      return;
    }
    if (!(tradeSizeUsd > 0)) {
      setError('Enter a margin amount greater than zero.');
      return;
    }
    if (!paper && !agentApproved && autoSign) {
      setError('Open HL Wallet (top right) and tap Approve agent (one MetaMask signature), then retry.');
      return;
    }
    let slippageBps;
    if (tradeOrderType === 'market') {
      slippageBps = resolveStoredHlSlippageBps();
      if (slippageBps == null) {
        setError('Set a valid slippage cap in HL Wallet settings (top right).');
        return;
      }
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/single-asset-hl/trade`,
        {
          symbol: tradeSymbol,
          side: tradeSide,
          action: tradeAction,
          sizeUsd: tradeSizeUsd,
          leverage: tradeLeverage,
          hlDex: tradeDex,
          hlCoin: tradeCoin,
          orderType: tradeOrderType,
          limitPrice: tradeOrderType === 'limit' ? tradeLimitPrice : undefined,
          slippageBps: tradeOrderType === 'market' ? slippageBps : undefined,
        },
        { headers: authHeaders() }
      );

      if (data.agentUsed || (!data.paperMode && data.success && !data.requiresBrowserSignature)) {
        setMessage(data.message || 'Live order submitted.');
        setPendingQuote(null);
        setRefreshKey((k) => k + 1);
        await loadStatus();
      } else if (data.requiresBrowserSignature && autoSign) {
        if (data.requiresAgentApproval || !agentApproved) {
          setError('Approve trading agent in HL Wallet settings (top right), then retry — one MetaMask signature.');
        } else {
          await signPreparedOrder(data);
        }
      } else if (data.requiresBrowserSignature || data.requiresQuoteConfirm) {
        setPendingQuote(data);
      } else {
        setMessage(data.message || 'Trade filled (paper).');
        setRefreshKey((k) => k + 1);
        await loadStatus();
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const cancelPendingTrade = async () => {
    const orderId = pendingQuote?.orderId;
    setBusy(true);
    setError(null);
    try {
      if (orderId) {
        await axios.post(`${API_BASE}/single-asset-hl/trade/${orderId}/cancel`, {}, { headers: authHeaders() });
      }
      setPendingQuote(null);
      setMessage('Trade cancelled.');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setPendingQuote(null);
    } finally {
      setBusy(false);
    }
  };

  const resetTradeForm = () => {
    setSizeUsd('0');
    setAction('open');
    setSide('long');
    setLimitPrice('');
    setMessage(null);
    setError(null);
  };

  const handleCancelTrade = async () => {
    if (pendingQuote) {
      await cancelPendingTrade();
      return;
    }
    resetTradeForm();
    setMessage('Order cleared.');
  };

  const confirmQuote = async () => {
    if (!pendingQuote) return;
    setBusy(true);
    setError(null);
    try {
      if (pendingQuote.requiresQuoteConfirm) {
        const { data } = await axios.post(
          `${API_BASE}/single-asset-hl/trade/${pendingQuote.orderId}/confirm`,
          {},
          { headers: authHeaders() }
        );
        setMessage(data.message || 'Trade filled (paper).');
        setPendingQuote(null);
        setRefreshKey((k) => k + 1);
        await loadStatus();
      } else if (pendingQuote.requiresAgentApproval || !agentApproved) {
        setError('Open HL Wallet → Approve agent (one MetaMask signature), then place the trade again.');
      } else {
        await signPreparedOrder(pendingQuote);
        setPendingQuote(null);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const applyLotToForm = (lot, { partial = true } = {}) => {
    const margin = lot.marginUsd ?? lot.sizeUsd ?? 0;
    selectMarket({ symbol: lot.symbol, dex: selected?.dex || routeDex || 'xyz' });
    setAction('close');
    setSide(lot.closeSide);
    setLeverage(lot.leverage || 1);
    setSizeUsd(String(margin));
  };

  const lotSummaryLine = (lot) => {
    const margin = lot.marginUsd ?? lot.sizeUsd ?? 0;
    const lev = lot.leverage || 1;
    const total = lot.notionalUsd != null ? lot.notionalUsd : margin * lev;
    return `Margin: ${fmtMarginUsd(margin)} Leverage ${lev}x (total ${fmtUsd(total)})`;
  };

  const fmtMarginUsd = (n) => {
    if (n == null || !Number.isFinite(n)) return '—';
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const onTradeLot = (lot) => {
    setPortfolioOpen(false);
    applyLotToForm(lot, { partial: true });
    const summary = lotSummaryLine(lot);
    setMessage(
      lot.side === 'long'
        ? `Partial sell — reduce ${lot.symbol} long. ${summary}. Adjust margin below for a partial close.`
        : `Partial buy — reduce ${lot.symbol} short. ${summary}. Adjust margin below for a partial close.`
    );
    setError(null);
  };

  const moveSpotUsdcToPerps = async () => {
    if (!status?.walletAddress || !(Number(spotUsdc) > 0.01)) {
      setError(
        'Nothing to move — spot USDC is already $0. If perps is also $0, funds are not on this Hyperliquid wallet.'
      );
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const amountUsd = Math.floor(Number(spotUsdc) * 100) / 100;
      setMessage('Approve the transfer in your wallet…');
      const result = await moveUsdcSpotToPerpsWithBrowserWallet({
        walletAddress: status.walletAddress,
        amountUsd,
        apiBase: API_BASE,
        authHeaders,
        toPerp: true,
      });
      if (!result?.verified && !result?.balances) {
        throw new Error('Transfer was not verified on Hyperliquid — balances unchanged.');
      }
      if (result?.balances) {
        setStatus((prev) =>
          prev
            ? {
                ...prev,
                ...result.balances,
                withdrawableUsd: result.balances.perpsUsdc ?? result.balances.withdrawableUsdc,
                withdrawableUsdc: result.balances.withdrawableUsdc,
                perpsUsdc: result.balances.perpsUsdc,
                perpsEquityUsdc: result.balances.perpsEquityUsdc,
                spotUsdc: result.balances.spotUsdc,
                spotSol: result.balances.spotSol,
              }
            : prev
        );
      }
      const perpsNow = result?.balances?.perpsUsdc;
      const spotNow = result?.balances?.spotUsdc;
      if (!(Number(perpsNow) > 0) && !(Number(spotNow) < amountUsd - 0.01)) {
        throw new Error(
          'Wallet signed, but Omni still sees funds on spot. Transfer did not complete on Hyperliquid.'
        );
      }
      setMessage(
        `Moved $${amountUsd.toFixed(2)} USDC from spot to perps` +
          (perpsNow != null ? ` — tradable balance now ${fmtUsd(perpsNow)}.` : '.')
      );
      setTimeout(() => {
        loadStatus().catch(() => {});
      }, 1500);
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      setError(msg);
      setMessage(null);
      window.alert(`Move to trading failed:\n\n${msg}`);
      await loadStatus().catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const onCloseLot = async (lot) => {
    setPortfolioOpen(false);
    setError(null);
    const dex = routeDex || selected?.dex || 'xyz';
    try {
      const { data: meta } = await axios.get(`${API_BASE}/single-asset-hl/market-meta/${lot.symbol}`, {
        headers: authHeaders(),
        params: { dex },
      });
      applyLotToForm(lot, { partial: false });
      await submitTrade({
        symbol: lot.symbol,
        side: lot.closeSide,
        action: 'close',
        sizeUsd: lot.marginUsd ?? lot.sizeUsd,
        leverage: lot.leverage || 1,
        hlDex: meta.dex || dex,
        hlCoin: meta.coin,
        orderType: 'market',
      });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const sideTabs = sideTabsForAction(action);

  return (
    <div className="sahl-page sahl-page-v2">
      <header className="sahl-header">
        <div>
          <p className="sahl-back">
            <Link to="/dashboard">← Dashboard</Link>
          </p>
          <h1>
            Single Asset Trading <span className="sahl-hl-badge">Hyperliquid</span>
          </h1>
          <p className="sahl-sub">
            Dedicated single-market trading — separate wallet &amp; positions from Multi-Asset manual/oBot.
          </p>
        </div>
        <div className="sahl-header-actions">
          <button type="button" className="sahl-portfolio-btn" onClick={() => setPortfolioOpen(true)}>
            Portfolio
            {status?.lots?.length != null
              ? ` (${status.lots.length})`
              : status?.positions?.length
                ? ` (${status.positions.length})`
                : ''}
          </button>
          <button type="button" className="sahl-portfolio-btn" onClick={() => setOrdersOpen(true)}>
            Order Transactions
            {status?.recentOrders?.length ? ` (${status.recentOrders.length})` : ''}
          </button>
          {!paper && status?.walletAddress && (
            <>
              <HlFundUsdcButton onClick={() => setFundModalOpen(true)} className="hl-fund-header-btn" />
              <HlFundUsdcButton
                onClick={() => setWithdrawModalOpen(true)}
                className="hl-fund-header-btn hl-withdraw-header-btn"
              >
                Withdraw
              </HlFundUsdcButton>
            </>
          )}
          <PageGearButton
            onClick={() => setWalletModalOpen(true)}
            label="HL Wallet"
            icon="wallet"
            showLabel
          />
        </div>
      </header>

      <WalletGearModal
        open={walletModalOpen}
        onClose={() => {
          setWalletModalOpen(false);
          loadStatus();
        }}
        title="Single Asset HL wallet"
        view="manual"
        apiPrefix="single-asset-hl"
      />

      <SingleAssetHlPortfolio
        open={portfolioOpen}
        onClose={() => setPortfolioOpen(false)}
        onTradeLot={onTradeLot}
        onCloseLot={onCloseLot}
        onStatusMessage={(msg, isErr) => (isErr ? setError(msg) : setMessage(msg))}
        refreshKey={refreshKey}
      />

      <SingleAssetHlOrderTransactions
        open={ordersOpen}
        onClose={() => setOrdersOpen(false)}
        onStatusMessage={(msg, isErr) => (isErr ? setError(msg) : setMessage(msg))}
        refreshKey={refreshKey}
      />

      <HlFundUsdcModal
        open={fundModalOpen}
        onClose={() => setFundModalOpen(false)}
        walletAddress={status?.walletAddress}
        variant="singleAsset"
        onFunded={() => {
          loadStatus();
          setMessage('USDC credited on Hyperliquid — you can retry live trading or approvals.');
        }}
      />

      <HlWithdrawModal
        open={withdrawModalOpen}
        onClose={() => setWithdrawModalOpen(false)}
        walletAddress={status?.walletAddress}
        onWithdrawn={() => {
          loadStatus();
          setRefreshKey((k) => k + 1);
          setMessage('Withdrawal submitted — USDC usually arrives on Arbitrum within a few minutes.');
        }}
      />

      {status?.commissionOwedUsd > 0 && (
        <div className="sahl-banner sahl-banner-warn">
          Commission owed: <strong>${status.commissionOwedUsd.toFixed(2)}</strong> (Single Asset HL)
          {status.commissionBlocked ? ' — live trading paused until cleared.' : ''}
        </div>
      )}

      <div className="sahl-mode-row">
        <span className={`sahl-mode-pill ${paper ? 'paper' : 'live'}`}>{paper ? 'PAPER' : 'LIVE'}</span>
        {status?.walletAddress ? (
          <button
            type="button"
            className="sahl-symbol-pill sahl-wallet-pill-btn"
            onClick={() => setWalletModalOpen(true)}
            title="Open HL Wallet settings"
          >
            Wallet {status.walletAddress.slice(0, 6)}…{status.walletAddress.slice(-4)}
            {!agentApproved && !paper ? ' · setup needed' : ''}
          </button>
        ) : (
          <button
            type="button"
            className="sahl-symbol-pill sahl-wallet-pill-btn sahl-wallet-pill-missing"
            onClick={() => setWalletModalOpen(true)}
          >
            Link HL Wallet
          </button>
        )}
        {selected && (
          <span className="sahl-symbol-pill">
            {marketLabel} · {selectedDex}
          </span>
        )}
      </div>

      {paper && (
        <div className="sahl-live-blurb sahl-paper-blurb">
          <strong>Paper mode</strong> — Click <em>Review paper trade</em> to see margin, fees, and platform
          commission before the simulated fill. Set slippage in HL Wallet settings (top right).
        </div>
      )}

      {!paper && (
        <div className="sahl-live-blurb">
          <strong>Live trading</strong> — Link MetaMask via <strong>HL Wallet</strong> (top right). Use{' '}
          <strong>Fund HL</strong> for deposit steps (min $5 USDC on Hyperliquid).{' '}
          {agentApproved ? (
            <>
              Click <strong>Review trade</strong> or <strong>{livePrimaryLabel}</strong> — orders submit
              without a MetaMask popup each time.
            </>
          ) : (
            <>
              First: <strong>HL Wallet</strong> → <strong>Approve agent</strong> (one MetaMask signature). Then
              click <strong>Review trade</strong> or <strong>Place market</strong>.
            </>
          )}
        </div>
      )}

      {!paper && status?.walletAddress && !agentApproved && (
        <div className="sahl-live-blurb sahl-agent-banner">
          <strong>One-time setup:</strong> Tap <strong>HL Wallet</strong> (top right) →{' '}
          <strong>Approve agent</strong>. MetaMask signs once — after that, live trades work without signing
          every order.
          <div className="sahl-agent-banner-actions">
            <button type="button" className="sahl-agent-banner-btn" onClick={() => setWalletModalOpen(true)}>
              Open HL Wallet
            </button>
          </div>
        </div>
      )}

      {error && <div className="sahl-error">{error}</div>}
      {message && <div className="sahl-success">{message}</div>}

      <div className="sahl-layout-v2">
        {selected && stats && (
          <div className="sahl-stats-bar sahl-stats-full">
            <div>
              <span className="label">Mark</span>
              <strong>{fmtDollar(stats.markPx)}</strong>
            </div>
            <div>
              <span className="label">Oracle</span>
              <strong>{fmtDollar(stats.oraclePx)}</strong>
            </div>
            <div>
              <span className="label">24h Change</span>
              <strong className={stats.changePct24h >= 0 ? 'up' : 'down'}>
                {stats.changePct24h != null ? `${stats.changePct24h >= 0 ? '+' : ''}${stats.changePct24h.toFixed(2)}%` : '—'}
              </strong>
            </div>
            <div>
              <span className="label">24h Volume</span>
              <strong>{fmtVolume(stats.volume24hUsd)}</strong>
            </div>
          </div>
        )}

        <div className="sahl-trading-row">
          <div className="sahl-chart-col">
            <HlTradingChart
              symbol={selected?.symbol}
              hlDex={selected?.dex || 'xyz'}
              hlCoin={selected?.coin}
              height={440}
            />
          </div>

          <div className="sahl-book-col">
            {selected?.symbol ? (
              <LiveOrderBook
                key={marketSelectionKey(selected)}
                symbol={selected.symbol}
                hlDex={selected.dex}
                hlCoin={selected.coin}
                apiPrefix="single-asset-hl"
                titlePrefix="Order book"
                compact
                dollarPrices
                spreadStepAdjustable
                pollMs={orderbookPollMs}
              />
            ) : (
              <div className="sahl-panel-placeholder">
                <p className="sahl-muted">Select a market below to view the order book.</p>
              </div>
            )}
          </div>

          <div className="sahl-trade-col">
            <div className="sahl-trade-form">
              <h3 className="sahl-trade-form-title">Place order</h3>
              {!selected?.symbol && (
                <p className="sahl-muted sahl-trade-hint">Pick an asset from the market finder below.</p>
              )}

              <div className="sahl-action-tabs">
                {ACTION_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={action === opt.value ? 'active' : ''}
                    onClick={() => setAction(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="sahl-side-tabs">
                {sideTabs.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    className={`${tab.value} ${side === tab.value ? 'active' : ''}`}
                    onClick={() => setSide(tab.value)}
                    title={tab.hint}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="sahl-order-type-tabs">
                <button type="button" className={orderType === 'market' ? 'active' : ''} onClick={() => setOrderType('market')}>
                  Market
                </button>
                <button type="button" className={orderType === 'limit' ? 'active' : ''} onClick={() => setOrderType('limit')}>
                  Limit
                </button>
              </div>

              {action === 'close' && (
                <p className="sahl-action-hint">
                  Closing uses <strong>Sell</strong> to exit a long and <strong>Cover</strong> to exit a short (reduce-only).
                </p>
              )}
              {action === 'add' && (
                <p className="sahl-action-hint">
                  Add increases an existing position in the same direction (long add or short add).
                </p>
              )}

              {selected?.symbol && (
                <p className="sahl-collateral-hint">
                  Margin is in <strong>{marginQuote}</strong> on dex <strong>{selectedDex}</strong>. The order
                  routes to Hyperliquid as <code>{selected.coin || marketLabel}</code>.
                </p>
              )}

              <div className="sahl-form-row">
                <label>Margin ({marginQuote})</label>
                <input type="number" min={0} value={sizeUsd} onChange={(e) => setSizeUsd(e.target.value)} />
              </div>

              <div className="sahl-form-row">
                <label>Leverage (max {selected?.maxLeverage ?? '—'}x)</label>
                <select value={leverage} onChange={(e) => setLeverage(Number(e.target.value))}>
                  {leverageOptions.map((lev) => (
                    <option key={lev} value={lev}>
                      {lev}x
                    </option>
                  ))}
                </select>
              </div>

              {orderType === 'limit' && (
                <div className="sahl-form-row">
                  <label>Limit price</label>
                  <input type="number" min={0} step="any" value={limitPrice} onChange={(e) => setLimitPrice(e.target.value)} />
                </div>
              )}

              <p className="sahl-notional">
                Notional ≈ <strong>{fmtUsd(notional)}</strong> ({leverage}x × {fmtUsd(Number(sizeUsd))}{' '}
                {marginQuote} margin)
              </p>
              {!paper && notional > 0 && notional < 10.5 && (
                <p className="sahl-action-hint">
                  Hyperliquid requires at least <strong>$10</strong> notional. On BTC, exact $10 often rounds
                  under the minimum — use about <strong>$11+</strong> margin at 1x (or higher leverage).
                </p>
              )}

              {status?.walletAddress ? (
                <div className="sahl-wallet-balance">
                  <div className="sahl-wallet-balance-head">
                    <h4 className="sahl-wallet-balance-title">Wallet balance availability</h4>
                    <button
                      type="button"
                      className="secondary-btn sahl-refresh-bal-btn"
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        setError(null);
                        try {
                          await loadStatus();
                          setMessage('Balances refreshed from Hyperliquid.');
                        } catch (e) {
                          setError(e.response?.data?.error || e.message);
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      Refresh
                    </button>
                  </div>
                  <p className="sahl-wallet-balance-addr">
                    {status.walletAddress.slice(0, 6)}…{status.walletAddress.slice(-4)}
                  </p>
                  <div className="sahl-wallet-balance-row">
                    <span>{unifiedAccount ? 'USDC (unified — tradable)' : 'USDC (perps free — tradable)'}</span>
                    <strong>
                      {perpsUsdc != null || status.withdrawableUsdc != null
                        ? fmtUsd(perpsUsdc ?? status.withdrawableUsdc)
                        : '…'}
                    </strong>
                  </div>
                  {!unifiedAccount && (
                    <div className="sahl-wallet-balance-row">
                      <span>USDC (perps equity)</span>
                      <strong>{perpsEquity != null ? fmtUsd(perpsEquity) : '…'}</strong>
                    </div>
                  )}
                  {!unifiedAccount && (
                    <div className="sahl-wallet-balance-row">
                      <span>USDC (spot — not tradable yet)</span>
                      <strong>{spotUsdc != null ? fmtUsd(spotUsdc) : '…'}</strong>
                    </div>
                  )}
                  {Number(spotSol) > 0 && (
                    <div className="sahl-wallet-balance-row">
                      <span>SOL (spot)</span>
                      <strong>{Number(spotSol).toFixed(4)}</strong>
                    </div>
                  )}
                  <div className="sahl-wallet-balance-row">
                    <span>USDH on Hyperliquid</span>
                    <strong>{hlUsdhBalance(status) != null ? fmtUsd(hlUsdhBalance(status)) : '…'}</strong>
                  </div>
                  {unifiedAccount && Number(spotUsdc) > 0.01 && (
                    <p className="sahl-wallet-balance-hint sahl-wallet-balance-hint-ok">
                      <strong>Unified account</strong> — Hyperliquid merged spot and perps.{' '}
                      <strong>{fmtUsd(spotUsdc)}</strong> is already tradable; no Move step needed.
                    </p>
                  )}
                  {canMoveSpotUsdc && (
                    <div className="sahl-wallet-balance-actions">
                      <p className="sahl-wallet-balance-hint">
                        You have <strong>{fmtUsd(spotUsdc)}</strong> on spot. Live trades only use{' '}
                        <strong>perps</strong> USDC. Click below and sign in your wallet (same 0x as Omni).
                        MetaMask, Rabby, and Phantom work best; Uniswap Wallet may not support this step.
                      </p>
                      <button
                        type="button"
                        className="sahl-submit long sahl-move-usdc-btn"
                        disabled={busy}
                        onClick={moveSpotUsdcToPerps}
                      >
                        {busy ? 'Confirm in wallet…' : `Move ${fmtUsd(spotUsdc)} to trading (sign in wallet)`}
                      </button>
                    </div>
                  )}
                  {readyToTrade && (
                    <p className="sahl-wallet-balance-hint sahl-wallet-balance-hint-ok">
                      {unifiedAccount
                        ? `Unified account — ${fmtUsd(perpsUsdc)} ready for live trading. Do not use Move.`
                        : 'Spot is empty and perps already has USDC — ready to trade. Use Refresh if numbers look stale.'}
                    </p>
                  )}
                  {!canMoveSpotUsdc && !readyToTrade && (
                    <p className="sahl-wallet-balance-hint">
                      No free USDC detected on this wallet yet. Deposit via Fund HL, then click Refresh.
                    </p>
                  )}
                  {selected?.symbol && (
                    <p className="sahl-wallet-balance-active">
                      This market uses <strong>{marginQuote}</strong> margin ({selectedDex}) — available:{' '}
                      <strong>
                        {collateralBalanceForMarket(status, selected) != null
                          ? fmtUsd(collateralBalanceForMarket(status, selected))
                          : '…'}
                      </strong>
                    </p>
                  )}
                </div>
              ) : (
                !paper && (
                  <div className="sahl-wallet-balance sahl-wallet-balance-muted">
                    <h4 className="sahl-wallet-balance-title">Wallet balance availability</h4>
                    <p>Link your HL wallet (top right: HL Wallet) to see USDC / USDH balances.</p>
                  </div>
                )
              )}

              <div className={`sahl-trade-actions${!paper ? ' sahl-trade-actions-live' : ''}`}>
                {paper ? (
                  <button
                    type="button"
                    className={`sahl-submit ${side}`}
                    disabled={busy || !selected}
                    onClick={() => submitTrade()}
                  >
                    {busy ? 'Submitting…' : 'Review paper trade'}
                  </button>
                ) : (
                  <div className="sahl-trade-primary-row">
                    <button
                      type="button"
                      className="sahl-review-btn secondary-btn"
                      disabled={busy || !selected}
                      onClick={() => submitTrade()}
                    >
                      {busy ? 'Preparing…' : 'Review trade'}
                    </button>
                    <button
                      type="button"
                      className={`sahl-submit ${side}`}
                      disabled={busy || !selected}
                      onClick={() => submitTrade(null, { autoSign: true })}
                      title={
                        agentApproved
                          ? 'Submit after balance checks (no wallet popup)'
                          : 'Approve agent in HL Wallet first'
                      }
                    >
                      {busy ? 'Submitting…' : livePrimaryLabel}
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="sahl-cancel-trade secondary-btn"
                  disabled={busy}
                  onClick={handleCancelTrade}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <SingleAssetHlPositionsPanel
          positions={status?.positions}
          lots={status?.lots}
          loading={!status}
        />

        <div className="sahl-markets-footer">
          <HlMarketSearch
            marketsApiPrefix="single-asset-hl"
            defaultSizeUsd={Number(sizeUsd) || 0}
            onSelectSymbol={(m) => selectMarket(m)}
            busy={busy}
            layout="footer"
            includeCrypto
            visibleMarketRows={10}
            dollarPrices
            selectedSymbolKey={selected ? (selected.coin || marketSelectionKey(selected)) : null}
          />
        </div>
      </div>

      {pendingQuote && (
        <div
          className="sahl-quote-overlay"
          role="presentation"
          onClick={() => !busy && cancelPendingTrade()}
        >
          <div
            className="sahl-quote-dialog"
            role="dialog"
            aria-labelledby="sahl-quote-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="sahl-quote-title">
              {pendingQuote.requiresQuoteConfirm ? 'Confirm paper trade' : 'Review trade'}
            </h2>
            <p className="sahl-quote-sub">
              {pendingQuote.requiresQuoteConfirm
                ? 'Review the simulated fill below. No wallet or Hyperliquid deposit is required — commission is recorded in paper mode only.'
                : 'Review margin, fees, and size below. When ready, sign with your wallet — the popup shows only a Hyperliquid signature request, not these details.'}
            </p>
            <dl className="sahl-quote-grid">
              <dt>Market</dt>
              <dd>
                {displayMarketName({ ...selected, symbol: pendingQuote.symbol, quote: pendingQuote.quote })}
                {' · '}
                {pendingQuote.hlDex || selectedDex}
              </dd>
              <dt>Side / action</dt>
              <dd>
                {String(pendingQuote.side).toUpperCase()} · {String(pendingQuote.actionType || action).toUpperCase()}
              </dd>
              <dt>Order type</dt>
              <dd>{pendingQuote.orderType === 'limit' ? 'Limit' : 'Market (IOC)'}</dd>
              <dt>Margin</dt>
              <dd>
                {fmtUsd(pendingQuote.deploy)} {pendingQuote.quote || marginQuote}
              </dd>
              <dt>Leverage</dt>
              <dd>{pendingQuote.leverage}x</dd>
              <dt>Notional</dt>
              <dd>≈ {fmtUsd(pendingQuote.notional)}</dd>
              <dt>Est. HL fee</dt>
              <dd>
                {pendingQuote.hlFeeUsd != null && pendingQuote.hlFeeUsd > 0
                  ? fmtUsd(pendingQuote.hlFeeUsd)
                  : '—'}
              </dd>
              {pendingQuote.orderType !== 'limit' && (
                <>
                  <dt>Slippage cap</dt>
                  <dd>{formatHlSlippageLabel(pendingQuote.slippageBps)}</dd>
                </>
              )}
              <dt>Platform commission</dt>
              <dd>{fmtCommissionLine(pendingQuote)}</dd>
              <dt>Size (contracts)</dt>
              <dd>{pendingQuote.size ?? '—'}</dd>
              <dt>{pendingQuote.orderType === 'limit' ? 'Limit price' : 'Worst price (slippage cap)'}</dt>
              <dd>{fmtDollar(pendingQuote.limitPx || pendingQuote.refPx)}</dd>
              <dt>HL coin id</dt>
              <dd>
                <code>{pendingQuote.hlCoin}</code>
              </dd>
            </dl>
            {!pendingQuote.requiresQuoteConfirm &&
              pendingQuote.commissionEnabled &&
              Number(pendingQuote.commissionUsd) > 0 && (
                <p className="sahl-quote-note">
                  Platform commission ({fmtUsd(pendingQuote.commissionUsd)}) is sent in USDC on Hyperliquid after
                  fill.
                </p>
              )}
            {pendingQuote.requiresQuoteConfirm &&
              pendingQuote.commissionEnabled &&
              Number(pendingQuote.commissionUsd) > 0 && (
                <p className="sahl-quote-note">
                  Paper mode: commission ({fmtUsd(pendingQuote.commissionUsd)}) is logged only — no USDC transfer.
                </p>
              )}
            <div className="sahl-quote-actions sahl-quote-actions-stack">
              <button
                type="button"
                className={`sahl-submit ${pendingQuote.side}`}
                disabled={busy}
                onClick={confirmQuote}
              >
                {busy
                  ? pendingQuote.requiresQuoteConfirm
                    ? 'Filling…'
                    : agentApproved
                      ? 'Submitting…'
                      : 'Signing…'
                  : pendingQuote.requiresQuoteConfirm
                    ? 'Confirm paper trade'
                    : pendingQuote.requiresAgentApproval || !agentApproved
                      ? 'Approve agent first (HL Wallet)'
                      : pendingQuote.orderType === 'limit'
                        ? 'Sign & place limit'
                        : 'Sign & place market'}
              </button>
              <button type="button" className="secondary-btn sahl-cancel-trade" disabled={busy} onClick={cancelPendingTrade}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
